import Vue from 'vue';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import {
    createScheduledReport,
    deleteScheduledReport,
    fetchScheduledReport,
    fetchScheduledReports,
    fetchSubscriptionSummary,
    updateScheduledReport,
} from './scheduled-reports.utils';

let loadAllPromise;

export default {
    namespaced: true,
    state: {
        activeReportId: '',
        activeScheduledReportId: '',
        map: {},
        stats: {},
        error: {},
        fetching: false,
    },
    mutations: {
        setActiveReport (state, { report }) {
            state.activeReportId = report.id;
        },
        setActiveScheduledReport (state, { report }) {
            state.activeScheduledReportId = report.id;
        },
        deleteMapKey (state, { id }) {
            Vue.delete(state.map, id);
        },
        deleteStatsKey (state, { id }) {
            Vue.delete(state.stats, id);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setMap (state, { map }) {
            state.map = map;
        },
        setStatsForReport (state, { id, report }) {
            Vue.set(state.stats, id, report);
        },
        setMapAtKey (state, { id, report }) {
            Vue.set(state.map, id, report);
        },
    },
    actions: {
        async loadAll ({ commit }, { reFetch = false, reportId } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            commit('setFetching', { fetching: true });
            loadAllPromise = fetchScheduledReports(reportId);
            commit('setActiveScheduledReport', { report: { id: undefined } });

            try {
                const { data: reports } = await loadAllPromise;
                commit('setFetching', { fetching: false });
                commit('setMap', { map: keyBy(reports, 'id') });
                if (reports.length > 0) {
                    commit('setActiveScheduledReport', { report: reports[0] });
                }
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async loadOne ({ commit, getters }, { reFetch = false, reportId, id }) {
            if (!reFetch && !!getters.reportById(id)) {
                return Promise.resolve();
            }

            commit('setFetching', { fetching: true });

            try {
                const { data: report } = await fetchScheduledReport(reportId, id);
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', { id: report.id, report });
                commit('setActiveScheduledReport', { report });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async create ({ commit }, { reportId, report } = {}) {
            commit('setFetching', { fetching: true });

            try {
                const { data: newReport } = await createScheduledReport(reportId, report);
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', { id: newReport.id, report: newReport });
                commit('setActiveScheduledReport', { report: newReport });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async update ({ commit }, { reportId, report }) {
            commit('setFetching', { fetching: true });

            try {
                const { data: newReport } = await updateScheduledReport(reportId, report);
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', { id: newReport.id, report: newReport });
                commit('setActiveScheduledReport', { report: newReport });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async remove ({ commit }, { reportId, id }) {
            commit('setFetching', { fetching: true });

            try {
                await deleteScheduledReport(reportId, id);
                commit('setFetching', { fetching: false });
                commit('deleteMapKey', { id });
                commit('setActiveScheduledReport', { report: { id: undefined } });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async loadStats ({ commit }, { reportId }) {
            commit('setFetching', { fetching: true });

            try {
                const { data: report } = await fetchSubscriptionSummary(reportId);
                commit('setStatsForReport', { id: reportId, report });
                commit('setFetching', { fetching: false });
                commit('setActiveReport', { report });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
    },
    getters: {
        activeScheduledReport: (state) => state.map[state.activeScheduledReportId] || {},
        activeReport: (state) => state.stats[state.activeReportId] || {},
        list: (state) => values(state.map),
        reportById: (state) => (id) => state.map[id],
        isFetching: (state) => state.fetching,
        isSubscribed: (state) => get(state.stats[state.activeReportId], 'subscriptionSummary.isSubscribed', false),
        totalSubscribers: (state) => get(state.stats[state.activeReportId], 'subscriptionSummary.totalSubscribers', 0),
    },
};

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}
